"use client";

import clsx from "clsx";
import { useEffect, useState, memo } from "react";
import Image from "next/image";

import Button from "@/shared/components/Button";
import appleDownloadBadge from "@public/apple_download_badge.svg";
import googleDownloadBadge from "@public/google_download_badge.svg";

import { PATHS } from "@/shared/utils/paths";
import {
  trackInstallStoreClick,
  type Store,
} from "@/shared/utils/analytics";

import s from "./styles.module.css";

enum OS {
  ios,
  android,
  other,
  notDetermined,
}

const ANDROID_LINK = ({ onClick }: { onClick?: (store: Store) => void }) => (
  <a
    className={clsx(s.link, s.google)}
    href={PATHS.googleplay}
    onClick={() => onClick?.("google_play")}
  >
    <Image
      fill
      src={googleDownloadBadge}
      alt="Google Play branded download button"
    />
  </a>
);

const IOS_LINK = ({ onClick }: { onClick?: (store: Store) => void }) => (
  <a
    className={clsx(s.link, s.apple)}
    href={PATHS.appstore}
    onClick={() => onClick?.("app_store")}
  >
    <Image
      fill
      src={appleDownloadBadge}
      alt="App Store branded download button"
    />
  </a>
);

export const useInstallLink = () => {
  const os = useOs();
  return os === OS.android ? PATHS.googleplay : PATHS.appstore;
};

export const PlainAppStoreButton = ({
  children,
  className,
  placement = "site",
}: {
  children: React.ReactNode;
  className?: string;
  placement?: string;
}) => {
  const os = useOs();
  const link = useInstallLink();
  const store: Store = os === OS.android ? "google_play" : "app_store";

  return (
    <Button
      className={className}
      href={link}
      onClick={() =>
        trackInstallStoreClick({
          store,
          placement,
          page_path: window.location.pathname,
        })
      }
    >
      {children}
    </Button>
  );
};

export const useOs = () => {
  const [os, setOs] = useState(OS.notDetermined);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase(),
      // macosPlatforms = /(macintosh|macintel|macppc|mac68k|macos)/i,
      // windowsPlatforms = /(win32|win64|windows|wince)/i,
      iosPlatforms = /(iphone|ipad|ipod)/i;

    if (iosPlatforms.test(userAgent)) {
      setOs(OS.ios);
      return;
    }
    // else if (macosPlatforms.test(userAgent)) {
    //   setOs("macos");
    // }
    // else if (windowsPlatforms.test(userAgent)) {
    //   setOs("windows");
    // }
    else if (/android/.test(userAgent)) {
      setOs(OS.android);
      return;
    }
    // else if (!os && /linux/.test(userAgent)) {
    //   setOs("linux");
    // }
    //
    //
    setOs(OS.other);
  }, []);

  return os;
};

interface Props {
  className?: string;
  onStoreClick?: (store: Store) => void;
  placement?: string;
}
function AppStoreButton({
  className,
  onStoreClick,
  placement = "site",
}: Props) {
  const os = useOs();
  const handleStoreClick = (store: Store) => {
    trackInstallStoreClick({
      store,
      placement,
      page_path: window.location.pathname,
    });
    onStoreClick?.(store);
  };

  if (os === OS.android) {
    return (
      <div className={s.container}>
        <ANDROID_LINK onClick={handleStoreClick} />
      </div>
    );
  }

  if (os === OS.ios) {
    return (
      <div className={s.container}>
        <IOS_LINK onClick={handleStoreClick} />
      </div>
    );
  }

  if (os === OS.other) {
    return (
      <div className={clsx(className, s.container)}>
        <IOS_LINK onClick={handleStoreClick} />
        <ANDROID_LINK onClick={handleStoreClick} />
      </div>
    );
  }

  return <div className={s.placeholder} />;
}

export default memo(AppStoreButton);
